import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import BranchTargetsSection from '@/components/BranchTargetsSection';
import axios from 'axios';
import {
  MapPin, Store, LogOut, Target, TrendingUp, DollarSign,
  ShoppingCart, Scissors, Package, BarChart3, Calendar,
  Users, Clock, UserCheck, UserX, AlertTriangle, Crown, CircleDot,
  Boxes, PackagePlus, AlertOctagon, Clock4, CheckCircle2, XCircle,
  TrendingDown, Activity, Bell, Percent, Trophy, ArrowRightLeft
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import UtilitySection from './zone-sections/UtilitySection';
import RankingSection from './zone-sections/RankingSection';
import InventorySection from './zone-sections/InventorySection';
import PersonnelSection from './zone-sections/PersonnelSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function KpiCard({ title, value, subtitle, icon: Icon, color = 'gold', testId }) {
  return (
    <Card className="bg-zinc-900/50 border-zinc-800 p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 text-${color}`} />
        <span className="text-xs text-zinc-500 uppercase tracking-wider">{title}</span>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      {subtitle && <p className="text-xs text-zinc-500 mt-1">{subtitle}</p>}
    </Card>
  );
}

function fmt(n) {
  if (n == null) return '$0';
  return '$' + Number(n).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export default function ZoneManagerDashboard() {
  const { user, logout, getAuthHeaders } = useAuth();
  const [zones, setZones] = useState([]);
  const [allZones, setAllZones] = useState([]);
  const [allBarbers, setAllBarbers] = useState([]);
  const [sales, setSales] = useState(null);
  const [staff, setStaff] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [utility, setUtility] = useState(null);
  const [ranking, setRanking] = useState(null);
  const [activeTab, setActiveTab] = useState('ventas');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [zonesRes, allZonesRes, salesRes, staffRes, invRes, utilRes, rankRes, allBarbersRes] = await Promise.all([
        axios.get(`${API}/zones`, getAuthHeaders()),
        axios.get(`${API}/zones?all=true`, getAuthHeaders()),
        axios.get(`${API}/zones/dashboard/sales`, getAuthHeaders()),
        axios.get(`${API}/zones/dashboard/staff`, getAuthHeaders()),
        axios.get(`${API}/zones/dashboard/inventory`, getAuthHeaders()),
        axios.get(`${API}/zones/dashboard/utility`, getAuthHeaders()),
        axios.get(`${API}/zones/dashboard/ranking`, getAuthHeaders()),
        axios.get(`${API}/barbers`, getAuthHeaders()),
      ]);
      setZones(zonesRes.data);
      setAllZones(allZonesRes.data);
      setAllBarbers(allBarbersRes.data);
      setSales(salesRes.data);
      setStaff(staffRes.data);
      setInventory(invRes.data);
      setUtility(utilRes.data);
      setRanking(rankRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const zone = zones[0];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="text-zinc-500">Cargando dashboard...</p>
      </div>
    );
  }

  if (!zone) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Card className="bg-zinc-900/50 border-zinc-800 p-12 text-center">
          <MapPin className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400 text-lg">No tienes zona asignada</p>
        </Card>
      </div>
    );
  }

  const today = sales?.today || {};
  const week = sales?.week || {};
  const month = sales?.month || {};
  const byBranch = sales?.by_branch || [];
  const dailyBreakdown = sales?.daily_breakdown || [];
  const goalProgress = sales?.goal_progress || 0;
  const monthlyTarget = sales?.monthly_target || 0;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gold/20 rounded-sm flex items-center justify-center">
              <MapPin className="w-4 h-4 text-gold" />
            </div>
            <div>
              <h1 className="text-base font-semibold text-white">{zone.name} &mdash; Dashboard</h1>
              <p className="text-xs text-zinc-500">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchData}
              className="text-zinc-400 hover:text-white text-xs"
              data-testid="refresh-btn"
            >
              Actualizar
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} className="text-zinc-400 hover:text-white" data-testid="zone-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 space-y-6">

        {/* Tab Navigation */}
        <div className="flex gap-1 bg-zinc-900/50 border border-zinc-800 rounded-sm p-1 w-fit" data-testid="dashboard-tabs">
          {[
            { id: 'ventas', label: 'Ventas', icon: TrendingUp },
            { id: 'staff', label: 'Colaboradores', icon: Users },
            { id: 'personnel', label: 'Gestión de Personal', icon: ArrowRightLeft },
            { id: 'inventario', label: 'Inventario', icon: Boxes },
            { id: 'ranking', label: 'Ranking', icon: Trophy },
            { id: 'utilidad', label: 'Utilidad y Alertas', icon: Activity },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm transition-colors ${
                activeTab === tab.id ? 'bg-gold/20 text-gold' : 'text-zinc-400 hover:text-white'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'ventas' && (<>

        {/* Goal Progress */}
        <Card className="bg-zinc-900/50 border-zinc-800 p-5" data-testid="goal-progress-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-gold" />
              <span className="text-sm text-zinc-300 font-medium">Meta Mensual Zona</span>
              {sales?.monthly_target_source === 'auto_sum' && (
                <span className="text-[10px] text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded" title="Suma automática de metas por sucursal">
                  Auto-suma
                </span>
              )}
              {sales?.monthly_target_source === 'manual' && (
                <span className="text-[10px] text-gold bg-gold/10 px-1.5 py-0.5 rounded">
                  Manual
                </span>
              )}
            </div>
            <span className="text-sm font-semibold text-white">
              {fmt(month.total)} / {fmt(monthlyTarget)}
            </span>
          </div>
          <Progress
            value={Math.min(goalProgress, 100)}
            className="h-3 bg-zinc-800"
          />
          <div className="flex justify-between mt-2">
            <span className="text-xs text-zinc-500">{goalProgress.toFixed(1)}% completado</span>
            <span className="text-xs text-zinc-500">
              Faltan {fmt(Math.max(monthlyTarget - (month.total || 0), 0))}
            </span>
          </div>
        </Card>

        {/* Per-branch goal progress */}
        <BranchTargetsSection byBranch={byBranch} />

        {/* Goal Breakdown — auto daily/weekly + pace */}
        <GoalBreakdownCard breakdown={sales?.goal_breakdown} />

        {/* Period KPIs */}
        <div>
          <h2 className="text-sm text-zinc-500 uppercase tracking-wider mb-3">Resumen de Ventas</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard title="Hoy" value={fmt(today.total)} subtitle={`${today.transactions || 0} transacciones`} icon={Calendar} testId="kpi-today" />
            <KpiCard title="Semana" value={fmt(week.total)} subtitle={`${week.transactions || 0} transacciones`} icon={BarChart3} testId="kpi-week" />
            <KpiCard title="Mes" value={fmt(month.total)} subtitle={`${month.transactions || 0} transacciones`} icon={TrendingUp} testId="kpi-month" />
            <KpiCard title="Ticket Promedio" value={fmt(month.avg_ticket)} subtitle="promedio mensual" icon={DollarSign} testId="kpi-avg-ticket" />
          </div>
        </div>

        {/* Services vs Products */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card className="bg-zinc-900/50 border-zinc-800 p-4" data-testid="services-card">
            <div className="flex items-center gap-2 mb-3">
              <Scissors className="w-4 h-4 text-green-500" />
              <span className="text-sm text-zinc-400">Servicios</span>
            </div>
            <p className="text-xl font-bold text-white">{fmt(month.services_revenue)}</p>
            <p className="text-xs text-zinc-500 mt-1">{month.services || 0} servicios vendidos este mes</p>
          </Card>
          <Card className="bg-zinc-900/50 border-zinc-800 p-4" data-testid="products-card">
            <div className="flex items-center gap-2 mb-3">
              <Package className="w-4 h-4 text-blue-500" />
              <span className="text-sm text-zinc-400">Productos</span>
            </div>
            <p className="text-xl font-bold text-white">{fmt(month.products_revenue)}</p>
            <p className="text-xs text-zinc-500 mt-1">{month.products || 0} productos vendidos este mes</p>
          </Card>
        </div>

        {/* Daily Sales Chart */}
        {dailyBreakdown.length > 0 && (
          <Card className="bg-zinc-900/50 border-zinc-800 p-5" data-testid="daily-chart-card">
            <h3 className="text-sm text-zinc-400 mb-4">Ventas Diarias del Mes</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyBreakdown}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: '#71717a', fontSize: 10 }}
                    tickFormatter={(d) => d.slice(8)}
                  />
                  <YAxis tick={{ fill: '#71717a', fontSize: 10 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 4 }}
                    labelStyle={{ color: '#a1a1aa' }}
                    formatter={(v) => [`$${Number(v).toLocaleString()}`, 'Ventas']}
                  />
                  <Bar dataKey="total" fill="#d4a853" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}

        {/* Branch Comparison */}
        {byBranch.length > 0 && (
          <Card className="bg-zinc-900/50 border-zinc-800 p-5" data-testid="branch-comparison-card">
            <h3 className="text-sm text-zinc-400 mb-4 flex items-center gap-2">
              <Store className="w-4 h-4" />
              Comparativo por Sucursal
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left text-zinc-500 py-2 px-3 font-medium">Sucursal</th>
                    <th className="text-right text-zinc-500 py-2 px-3 font-medium">Hoy</th>
                    <th className="text-right text-zinc-500 py-2 px-3 font-medium">Mes</th>
                    <th className="text-right text-zinc-500 py-2 px-3 font-medium">Ticket Prom.</th>
                    <th className="text-right text-zinc-500 py-2 px-3 font-medium">Transacciones</th>
                    <th className="text-right text-zinc-500 py-2 px-3 font-medium">Servicios</th>
                    <th className="text-right text-zinc-500 py-2 px-3 font-medium">Productos</th>
                  </tr>
                </thead>
                <tbody>
                  {byBranch.map((b) => (
                    <tr key={b.branch_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-gold rounded-full" />
                          <span className="text-zinc-200">{b.branch_name}</span>
                        </div>
                      </td>
                      <td className="text-right py-3 px-3 text-zinc-300 font-medium">{fmt(b.today?.total)}</td>
                      <td className="text-right py-3 px-3 text-white font-semibold">{fmt(b.month?.total)}</td>
                      <td className="text-right py-3 px-3 text-zinc-300">{fmt(b.month?.avg_ticket)}</td>
                      <td className="text-right py-3 px-3 text-zinc-400">{b.month?.transactions || 0}</td>
                      <td className="text-right py-3 px-3 text-green-400">{b.month?.services || 0}</td>
                      <td className="text-right py-3 px-3 text-blue-400">{b.month?.products || 0}</td>
                    </tr>
                  ))}
                </tbody>
                {byBranch.length > 1 && (
                  <tfoot>
                    <tr className="border-t border-zinc-700 bg-zinc-800/30">
                      <td className="py-3 px-3 text-zinc-300 font-semibold">Total Zona</td>
                      <td className="text-right py-3 px-3 text-zinc-300 font-medium">{fmt(today.total)}</td>
                      <td className="text-right py-3 px-3 text-white font-bold">{fmt(month.total)}</td>
                      <td className="text-right py-3 px-3 text-zinc-300">{fmt(month.avg_ticket)}</td>
                      <td className="text-right py-3 px-3 text-zinc-400">{month.transactions || 0}</td>
                      <td className="text-right py-3 px-3 text-green-400">{month.services || 0}</td>
                      <td className="text-right py-3 px-3 text-blue-400">{month.products || 0}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </Card>
        )}

        {/* Empty state for no sales data */}
        {month.transactions === 0 && (
          <Card className="bg-zinc-900/50 border-zinc-800 border-dashed p-8">
            <div className="text-center">
              <ShoppingCart className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">No hay ventas registradas este mes en tus sucursales</p>
              <p className="text-zinc-600 text-sm mt-1">Las métricas se actualizarán conforme se registren ventas en el POS</p>
            </div>
          </Card>
        )}

        </>)}

        {/* =================== STAFF TAB =================== */}
        {activeTab === 'staff' && (<>
          <StaffSection staff={staff} />
        </>)}

        {/* =================== PERSONNEL TAB (Gestión de Personal) =================== */}
        {activeTab === 'personnel' && (<>
          <PersonnelSection
            staff={staff}
            zones={zones}
            allZones={allZones}
            allBarbers={allBarbers}
            onChanged={fetchData}
            getAuthHeaders={getAuthHeaders}
          />
        </>)}

        {/* =================== INVENTORY TAB =================== */}
        {activeTab === 'inventario' && (<>
          <InventorySection inventory={inventory} onChanged={fetchData} getAuthHeaders={getAuthHeaders} />
        </>)}

        {/* =================== RANKING TAB =================== */}
        {activeTab === 'ranking' && (<>
          <RankingSection ranking={ranking} />
        </>)}

        {/* =================== UTILITY TAB =================== */}
        {activeTab === 'utilidad' && (<>
          <UtilitySection utility={utility} />
        </>)}

      </main>
    </div>
  );
}

/* =================== STAFF SECTION COMPONENT =================== */
function StaffSection({ staff }) {
  const employees = staff?.employees || [];
  const summary = staff?.attendance_summary || {};

  const statusLabel = (s) => {
    if (s === 'working') return { text: 'Trabajando', cls: 'text-green-400 bg-green-500/10' };
    if (s === 'checked_out') return { text: 'Salió', cls: 'text-blue-400 bg-blue-500/10' };
    return { text: 'Ausente', cls: 'text-red-400 bg-red-500/10' };
  };

  return (
    <div className="space-y-4">
      {/* Attendance Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-zinc-900/50 border-zinc-800 p-4" data-testid="staff-total">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-gold" />
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Total</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.total_employees || 0}</p>
          <p className="text-xs text-zinc-500">colaboradores</p>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800 p-4" data-testid="staff-present">
          <div className="flex items-center gap-2 mb-2">
            <UserCheck className="w-4 h-4 text-green-500" />
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Presentes Hoy</span>
          </div>
          <p className="text-2xl font-bold text-green-400">{summary.present_today || 0}</p>
          <p className="text-xs text-zinc-500">de {summary.total_employees || 0}</p>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800 p-4" data-testid="staff-absent">
          <div className="flex items-center gap-2 mb-2">
            <UserX className="w-4 h-4 text-red-500" />
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Ausentes Hoy</span>
          </div>
          <p className="text-2xl font-bold text-red-400">{summary.absent_today || 0}</p>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800 p-4" data-testid="staff-late">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-yellow-500" />
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Retardos (Mes)</span>
          </div>
          <p className="text-2xl font-bold text-yellow-400">{summary.total_late_month || 0}</p>
        </Card>
      </div>

      {/* Employee Ranking Table */}
      <Card className="bg-zinc-900/50 border-zinc-800 p-5" data-testid="staff-ranking-card">
        <h3 className="text-sm text-zinc-400 mb-4 flex items-center gap-2">
          <Crown className="w-4 h-4 text-gold" />
          Ranking de Colaboradores — Mes Actual
        </h3>
        {employees.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">No hay colaboradores en tus sucursales</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left text-zinc-500 py-2 px-3 font-medium w-8">#</th>
                  <th className="text-left text-zinc-500 py-2 px-3 font-medium">Colaborador</th>
                  <th className="text-left text-zinc-500 py-2 px-3 font-medium">Sucursal</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Ventas</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Clientes</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Servicios</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Productos</th>
                  <th className="text-center text-zinc-500 py-2 px-3 font-medium">Asist.</th>
                  <th className="text-center text-zinc-500 py-2 px-3 font-medium">Retardos</th>
                  <th className="text-center text-zinc-500 py-2 px-3 font-medium">Faltas</th>
                  <th className="text-center text-zinc-500 py-2 px-3 font-medium">Hoy</th>
                  <th className="text-center text-zinc-500 py-2 px-3 font-medium">Metas</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp, idx) => {
                  const status = statusLabel(emp.today_status);
                  return (
                    <tr key={emp.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                      <td className="py-3 px-3">
                        {idx === 0 ? (
                          <Crown className="w-4 h-4 text-gold" />
                        ) : (
                          <span className="text-zinc-600 text-xs">{idx + 1}</span>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <CircleDot className={`w-3 h-3 ${emp.today_status === 'working' ? 'text-green-500' : emp.today_status === 'checked_out' ? 'text-blue-500' : 'text-zinc-600'}`} />
                          <div>
                            <span className="text-zinc-200 text-sm">{emp.name}</span>
                            <span className="text-xs text-zinc-600 ml-2">{emp.role_type === 'stylist' ? 'Estilista' : 'Barbero'}</span>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-3 text-xs text-zinc-500">{emp.branch_name}</td>
                      <td className="text-right py-3 px-3 text-white font-semibold">{fmt(emp.revenue)}</td>
                      <td className="text-right py-3 px-3 text-zinc-300">{emp.clients}</td>
                      <td className="text-right py-3 px-3 text-green-400">{emp.services}</td>
                      <td className="text-right py-3 px-3 text-blue-400">{emp.products}</td>
                      <td className="text-center py-3 px-3 text-zinc-300">
                        {emp.days_present}/{emp.expected_days}
                      </td>
                      <td className="text-center py-3 px-3">
                        <span className={emp.late > 0 ? 'text-yellow-400' : 'text-zinc-600'}>{emp.late}</span>
                      </td>
                      <td className="text-center py-3 px-3">
                        <span className={emp.absences > 0 ? 'text-red-400' : 'text-zinc-600'}>{emp.absences}</span>
                      </td>
                      <td className="text-center py-3 px-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${status.cls}`}>
                          {status.text}
                        </span>
                      </td>
                      <td className="text-center py-3 px-3">
                        <GoalsCell emp={emp} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/* =================== BRANCH TARGETS SECTION (imported from shared component) =================== */
// See /app/frontend/src/components/BranchTargetsSection.jsx

/* =================== GOAL BREAKDOWN CARD (weekly/daily + pace) =================== */
function GoalBreakdownCard({ breakdown }) {
  if (!breakdown || (breakdown.monthly_target || 0) <= 0) return null;

  const fmt = (n) => '$' + Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const ahead = breakdown.pace_status === 'ahead';
  const paceColor = ahead ? 'text-green-400' : 'text-red-400';
  const paceBg = ahead ? 'bg-green-500/10 border-green-500/40' : 'bg-red-500/10 border-red-500/40';
  const today = breakdown.actual_today || 0;
  const dailyTarget = breakdown.daily_target || 0;
  const todayPct = breakdown.today_progress_pct || 0;

  return (
    <Card className="bg-zinc-900/50 border-zinc-800 p-5 space-y-4" data-testid="goal-breakdown-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-gold" />
          <span className="text-sm text-zinc-300 font-medium">Desglose de Meta</span>
        </div>
        <span className="text-[10px] text-zinc-500">
          Día {breakdown.current_day} / {breakdown.days_in_month}
        </span>
      </div>

      {/* Auto targets row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-zinc-800/40 rounded-sm p-3" data-testid="bd-monthly">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Meta Mensual</p>
          <p className="text-base font-bold text-white mt-1">{fmt(breakdown.monthly_target)}</p>
        </div>
        <div className="bg-zinc-800/40 rounded-sm p-3" data-testid="bd-weekly">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Meta Semanal (auto)</p>
          <p className="text-base font-bold text-gold mt-1">{fmt(breakdown.weekly_auto_target)}</p>
          <p className="text-[10px] text-zinc-600 mt-0.5">{breakdown.weeks_in_month} semanas</p>
        </div>
        <div className="bg-zinc-800/40 rounded-sm p-3" data-testid="bd-daily">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Meta Diaria</p>
          <p className="text-base font-bold text-blue-400 mt-1">{fmt(dailyTarget)}</p>
          <p className="text-[10px] text-zinc-600 mt-0.5">Hoy: {fmt(today)} ({todayPct}%)</p>
        </div>
      </div>

      {/* Pace indicator */}
      <div className={`rounded-sm border p-3 ${paceBg}`} data-testid="pace-indicator">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-zinc-400">Ritmo actual</p>
            <p className={`text-base font-bold ${paceColor}`}>
              {ahead ? '↑ Vas adelantado por ' : '↓ Vas atrasado por '}
              {fmt(Math.abs(breakdown.pace_delta))}
            </p>
          </div>
          <div className="text-right text-[11px] text-zinc-500">
            <p>Esperado al día {breakdown.current_day}:</p>
            <p className="text-zinc-300">{fmt(breakdown.expected_today)}</p>
            <p>Real:</p>
            <p className="text-zinc-300">{fmt(breakdown.actual_month)}</p>
          </div>
        </div>
      </div>

      {/* Weekly breakdown */}
      <div data-testid="weekly-buckets">
        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Por Semana</p>
        <div className="space-y-1.5">
          {(breakdown.weeks || []).map(w => (
            <div key={w.week_num} className="flex items-center gap-3 text-xs" data-testid={`week-bucket-${w.week_num}`}>
              <span className="text-zinc-400 w-16">Sem. {w.week_num}</span>
              <span className="text-[10px] text-zinc-600 w-16">d{w.start_day}–{w.end_day}</span>
              <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className={`h-full ${w.progress_pct >= 100 ? 'bg-green-500' : w.progress_pct >= 70 ? 'bg-gold' : w.progress_pct >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                  style={{ width: `${Math.min(w.progress_pct, 100)}%` }}
                />
              </div>
              <span className="text-zinc-300 w-24 text-right">
                {fmt(w.actual)} / {fmt(w.target)}
              </span>
              <span className="w-12 text-right text-zinc-500">{w.progress_pct.toFixed(0)}%</span>
              {w.is_override && (
                <span className="text-[10px] px-1 py-0.5 rounded bg-gold/20 text-gold" title="Meta manual">★</span>
              )}
            </div>
          ))}
        </div>
        <p className="text-[10px] text-zinc-600 mt-2">★ = meta semanal personalizada (override manual)</p>
      </div>
    </Card>
  );
}


/* =================== GOALS CELL (per-barber compact progress) =================== */
function GoalsCell({ emp }) {
  const g = emp.goals || {};
  const p = emp.progress_pct || {};
  const hasAny = (g.avg_ticket || 0) > 0 || (g.services || 0) > 0 || (g.products_revenue || 0) > 0;
  if (!hasAny) {
    return <span className="text-[10px] text-zinc-600">Sin metas</span>;
  }
  const items = [
    { key: 'avg_ticket', label: 'Ticket', pct: p.avg_ticket },
    { key: 'services', label: 'Serv.', pct: p.services },
    { key: 'products_revenue', label: 'Prod.', pct: p.products_revenue },
  ].filter(x => x.pct != null);
  const colorClass = (pct) => pct >= 100 ? 'bg-green-500 text-white' : pct >= 70 ? 'bg-gold/30 text-gold' : pct >= 40 ? 'bg-yellow-500/20 text-yellow-300' : 'bg-red-500/20 text-red-300';
  return (
    <div className="flex flex-col gap-1 items-center" data-testid={`goals-cell-${emp.id}`}>
      {items.map(it => (
        <span
          key={it.key}
          title={`${it.label}: ${it.pct}%`}
          className={`text-[10px] px-1.5 py-0.5 rounded-sm whitespace-nowrap ${colorClass(it.pct)}`}
        >
          {it.label} {it.pct}%
        </span>
      ))}
    </div>
  );
}


